import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useState } from 'react';

import useUtmsFromSession from 'hooks/useUtmsFromSession';

import { sendEvent } from 'lib/gtag';
import {
  toCouponStoreWithCouponIdLink,
  toPromobitCouponsWithCouponIdLink,
  toRedirectCouponStoreLink,
} from 'lib/links';
import { isNumber } from 'lib/number';

import useDialog from './useDialog';

import LINKS from 'constants/links';

const PickCouponDialog = dynamic(
  () => import('components/Coupons/PickCouponDialog'),
  {
    ssr: false,
  }
);

/**
 * Some stores punish us when opening tabs in background to increase the
 * quantity of page views since this can "artificially" increase the amount of
 * our revenue. So, for contractual reasons we need to disable this behavior
 * only for these specific stores.
 */
const PROHIBITED_STORES_TO_OPEN_NEW_TABS = ['mercado-livre'];
const SECONDS_TO_WAIT_PAGE_LOAD_TO_OPEN_COUPON_PICKER = 250;

const useCouponPicker = (gaEventCategory) => {
  const router = useRouter();
  const { showDialog } = useDialog();
  const [isCouponsCodesVisible, setCouponsCodesVisible] = useState(false);
  const { utmParams } = useUtmsFromSession();

  useEffect(() => {
    const { hash } = window.location;

    if (!hash?.startsWith('#cupom:')) {
      return;
    }

    const couponId = Number(hash.split(':')[1]);

    if (!couponId || !isNumber(couponId) || couponId === 0) {
      return;
    }

    setCouponsCodesVisible(true);
    setTimeout(() => {
      showDialog(PickCouponDialog, { couponId });
    }, SECONDS_TO_WAIT_PAGE_LOAD_TO_OPEN_COUPON_PICKER);
  }, [router]);

  const openCouponPicker = useCallback(
    ({ couponId, storeName, storeSlug }) => {
      sendEvent({
        action: 'pick_coupon',
        category: gaEventCategory,
        data: storeName,
      });

      const couponPickerUrl = toCouponStoreWithCouponIdLink(
        couponId,
        storeSlug
      );
      const isStoreProhibitedToOpenNewTabs =
        PROHIBITED_STORES_TO_OPEN_NEW_TABS.includes(storeSlug);

      if (isStoreProhibitedToOpenNewTabs) {
        router.push(couponPickerUrl, null, { shallow: true });
        return;
      }

      const storeLink =
        LINKS.PROMOBIT_COUPONS === router.asPath
          ? toPromobitCouponsWithCouponIdLink(couponId)
          : toCouponStoreWithCouponIdLink(couponId, storeSlug);

      window.open(storeLink, '_blank');
      window.open(toRedirectCouponStoreLink(couponId, utmParams), '_self');
    },
    [gaEventCategory, router]
  );

  const retrieveCoupon = useCallback(
    ({ couponId, storeName, storeSlug }) => {
      sendEvent({
        action: 'retrieve_coupon',
        category: gaEventCategory,
        data: storeName,
      });

      const couponPickerUrl = toCouponStoreWithCouponIdLink(
        couponId,
        storeSlug
      );
      router.push(couponPickerUrl, null, { shallow: true });
    },
    [gaEventCategory]
  );

  return { openCouponPicker, retrieveCoupon, isCouponsCodesVisible };
};

export default useCouponPicker;
